import { cn } from "@/lib/utils";
import type { TemplatedText, TiptapDoc } from "@repo/shared/text";
import type { EditorEvents } from "@tiptap/core";
import { Mention } from "@tiptap/extension-mention";
import { EditorContent, ReactRenderer, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import _ from "lodash";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import tippy from "tippy.js";

export function tiptapToPlaintext(
  content: string | Record<string, unknown> | null | undefined,
): string {
  function loadJsonFromStrIfPossible(
    node: string | Record<string, unknown>,
  ): string | Record<string, unknown> {
    if (typeof node === "string") {
      try {
        return JSON.parse(node);
      } catch {
        return node;
      }
    }
    return node;
  }

  const node = loadJsonFromStrIfPossible(content ?? "");

  if (typeof node === "string") {
    // Copied comment from python repo
    // # NOTE(sharabesh): Old instances of tiptap will not be migrated to the new representation
    // # We lazily migrate after eachone is edited, but in case it's not yet migrated, we're good to go
    // Handle legacy string content
    return node;
  }

  if (Array.isArray(node)) {
    return node.map((child) => tiptapToPlaintext(child)).join("");
  }

  if (typeof node === "object" && node !== null) {
    const nodeType = node.type as string | undefined;
    const nodeContent =
      (node.content as Record<string, unknown>[] | undefined) ?? [];
    const text = (node.text as string | undefined) ?? "";

    if (nodeType === "mention") {
      const attrs = node.attrs as Record<string, unknown> | undefined;
      const mentionedId = attrs?.id ?? "unknown";
      return `@${mentionedId}`;
    }

    if (
      nodeType === "text" ||
      nodeType === "paragraph" ||
      nodeType === "heading"
    ) {
      return (
        text + nodeContent.map((child) => tiptapToPlaintext(child)).join("")
      );
    }

    return nodeContent.map((child) => tiptapToPlaintext(child)).join("");
  }

  return "";
}

function parseAsJsonOrString(stringOrJson: TemplatedText) {
  // NOTE(Sharabesh): Our backend stores a simple string representation
  // of the data in our database. This can either be inferred as JSON
  // in the new world or the old static string content. Here we try to interpret what this is
  try {
    if (typeof stringOrJson === "string") {
      return JSON.parse(stringOrJson);
    }

    return stringOrJson;
  } catch (e) {
    return stringOrJson;
  }
}

interface SuggestionsDropDownProps<T> {
  items: T[];
  command: (item: { id: T }) => void;
}

interface SuggestionsDropDownHandle {
  onKeyDown: (args: { event: React.KeyboardEvent }) => boolean;
}

const SuggestionsDropDown = forwardRef<
  SuggestionsDropDownHandle,
  SuggestionsDropDownProps<string>
>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) {
      props.command({ id: item });
    }
  };

  const upHandler = () => {
    setSelectedIndex(
      (selectedIndex + props.items.length - 1) % props.items.length,
    );
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length);
  };

  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  // Set focus on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to re-render when the items change
  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
      if (event.key === "ArrowUp") {
        upHandler();
        return true;
      }

      if (event.key === "ArrowDown") {
        downHandler();
        return true;
      }

      if (event.key === "Enter") {
        enterHandler();
        return true;
      }

      return false;
    },
  }));

  return (
    <div className="bg-white border border-gray-1 rounded-lg shadow flex flex-col gap-0.5 overflow-auto p-1 relative w-64">
      {props.items.length > 0 ? (
        props.items.map((item, index) => (
          <button
            type="button"
            className={cn("hover:bg-neutral-200 rounded-md p-2", {
              "bg-blue-100": selectedIndex === index,
            })}
            key={item}
            onClick={() => selectItem(index)}
          >
            {item}
          </button>
        ))
      ) : (
        <div className="item">No results</div>
      )}
    </div>
  );
});

const TextEditor = (props: {
  content: TemplatedText;
  className?: string;
  editorClassName?: string;
  onChange: (update: TiptapDoc) => void;
  placeholder: string;
  // Used to autofill mentioned extractions
  extractions: Set<string>;
}) => {
  const { content, className, onChange, extractions, editorClassName } = props;

  const parsedContent = parseAsJsonOrString(content);

  const filterSuggestions = useCallback(
    (props: { query: string }) => {
      const { query } = props;
      const extractionsArr = [...extractions];
      return extractionsArr
        .filter((key) => {
          if (!query || query === "") {
            // No query - show everything
            return true;
          }
          return key.trim().toLowerCase().startsWith(query.toLowerCase());
        })
        .slice(0, 5);
    },
    [extractions],
  );

  const onUpdate = useCallback((update: EditorEvents["update"]) => {
    const { editor } = update;
    editor.getJSON();
    onChange(editor.getJSON() as TiptapDoc);
  }, []);

  const tipTapEditor = useEditor({
    extensions: [
      StarterKit,
      Mention.configure({
        deleteTriggerWithBackspace: true,
        HTMLAttributes: {
          class: "bg-blue-100 text-black rounded-md break-all px-1 py-0.5",
        },
        suggestion: {
          items: filterSuggestions,
          render: () => {
            let reactRenderer: ReactRenderer;
            let popup: any;

            return {
              onStart: (props) => {
                reactRenderer = new ReactRenderer(SuggestionsDropDown, {
                  props,
                  editor: props.editor,
                });

                if (!props.clientRect) {
                  return;
                }

                popup = tippy("body", {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: reactRenderer.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: "manual",
                  placement: "bottom-start",
                });
              },

              onUpdate(props) {
                reactRenderer.updateProps(props);

                if (!props.clientRect) {
                  return;
                }

                popup[0].setProps({
                  getReferenceClientRect: props.clientRect,
                });
              },

              onKeyDown(props) {
                if (props.event.key === "Escape") {
                  popup[0].hide();
                  return true;
                }

                return reactRenderer?.ref?.onKeyDown(props);
              },

              onExit() {
                popup.forEach((pop: any) => pop.destroy());
                reactRenderer.destroy();
              },
            };
          },
        },
      }),
    ],
    content: parsedContent,
    onUpdate,
    editorProps: {
      attributes: {
        // Add some padding to the surrounding box
        class: cn(editorClassName, "p-1"),
      },
    },
  });

  return (
    <div
      className={cn(className, {
        "cursor-pointer": !tipTapEditor?.isFocused,
      })}
    >
      <EditorContent editor={tipTapEditor} />
    </div>
  );
};

export default TextEditor;
